<template>
    <v-card color="alternateCard">
        <v-toolbar dark color="primary">
            <v-btn icon dark :disabled="saving" @click="cancelEditSupplierProducts">
                <v-icon large>close</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-toolbar-title v-if="editedSupplier && editedSupplier.ID">Edit Supplier Products</v-toolbar-title>
            <v-toolbar-title v-else>Add New Supplier</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
                
            </v-toolbar-items>
        </v-toolbar>
        <v-layout class="dialog-content">
            <v-flex xs12 md6 offset-md3>
                <v-card v-if="loading || loadError">
                    <v-layout v-if="loading" wrap>
                        <v-progress-linear color="primary" indeterminate></v-progress-linear>
                    </v-layout>
                    <v-layout v-if="loadError" wrap>
                        <v-flex xs12>
                            <error-alert content="There was an error loading the required data. Please try again." :display="loadError"></error-alert>
                        </v-flex>
                        <v-flex xs12>
                            <error-alert :content="loadError" :display="loadError"></error-alert>
                        </v-flex>
                    </v-layout>
                </v-card>
                <v-card v-if="!loading && !loadError">
                    <v-layout wrap>
                        <v-flex xs12>
                            <h1 class="text-xs-center">{{this.editedSupplier.Name}} - Products</h1>
                        </v-flex>
                        <v-flex xs12 sm8>
                            <v-autocomplete
                                :items="unselectedCategories"
                                item-text="Name"
                                item-value="ID"
                                v-model="newCategory"
                                label="Add Category"
                                hint="Add entire category of products"
                                return-object
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm4>
                            <v-btn
                                color="success"
                                block
                                @click="addCategory()"
                            >
                                Add Category
                            </v-btn>
                        </v-flex>
                        <v-flex xs12 sm8>
                            <v-autocomplete
                                :items="unselectedProducts"
                                item-text="Name"
                                item-value="ID"
                                v-model="newProduct"
                                label="Add Product"
                                hint="Add individual product"
                                return-object
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm4>
                            <v-btn
                                color="success"
                                block
                                @click="addProduct()"
                            >
                                Add Product
                            </v-btn>
                        </v-flex>
                    </v-layout>
                </v-card>
                <v-card v-if="!loading && !loadError">
                    <v-flex xs12>
                        <h2 class="text-xs-center">Selected Categories</h2>
                        <p class="text-xs-center">All products in these categories are available for ordering from this supplier, including products added to the selected categories later.</p>
                        <v-data-table
                            :headers="headers.selectedCategories"
                            :items="supplierProductsByCategory"
                            must-sort
                            :rows-per-page-items="rowsPerPage"
                            :pagination.sync="pagination.selectedCategories"
                        >
                            <template slot="items" slot-scope="props">
                                <tr :data-id="props.item.ID">
                                    <td>{{props.item.CategoryName}}</td>
                                    <td>
                                        <v-btn
                                            color="error"
                                            block
                                            @click="removeCategory(props.item)"
                                        >
                                            Remove
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-card>
                <v-card v-if="!loading && !loadError">
                    <v-flex xs12>
                        <h2 class="text-xs-center">Selected Products</h2>
                        <v-data-table
                            :headers="headers.selectedProducts"
                            :items="supplierProductsByIndividual"
                            must-sort
                            :rows-per-page-items="rowsPerPage"
                            :pagination.sync="pagination.selectedProducts"
                        >
                            <template slot="items" slot-scope="props">
                                <tr :data-id="props.item.ID">
                                    <td>{{props.item.CategoryName}}</td>
                                    <td>{{props.item.ProductName}}</td>
                                    <td>
                                        <v-btn
                                            color="error"
                                            block
                                            @click="removeProduct(props.item)"
                                        >
                                            Remove
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-card>
                <v-card>
                    <v-layout wrap class="dialog-action-buttons">
                        <v-flex xs12 sm6>
                            <v-btn block color="error" :disabled="saving" @click="cancelEditSupplierProducts">Cancel</v-btn>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-btn block color="success" :disabled="!!loadError" :loading="saving" @click="saveEditedSupplierProducts">Save</v-btn>
                        </v-flex>
                        <v-flex xs12>
                            <error-alert :content="saveError" :display="saveError"></error-alert>
                        </v-flex>
                    </v-layout>    
                </v-card>                        
            </v-flex>
        </v-layout>
    </v-card>
</template>

<script>
import ErrorAlert from "../common/ErrorAlert"

export default {
    components: {
        ErrorAlert
    },
    props: [
        "editedSupplier"
    ],
    data() {
        return {
            newCategory: {},
            newProduct: {},
            rowsPerPage: [20, 50, 100],
            pagination: {
                selectedCategories: {
                    rowsPerPage: 20, // default
                    sortBy: "CategoryName"
                },
                selectedProducts: {
                    rowsPerPage: 20, // default
                    sortBy: "CategoryName"
                }
            },
            headers: {
                selectedCategories: [
                    {
                        text: "Category Name",
                        value: "CategoryName"
                    },
                    {
                        text: "", // remove button
                        sortable: false
                    }
                ],
                selectedProducts: [
                    {
                        text: "Category Name",
                        value: "CategoryName"
                    },
                    {
                        text: "Product Name",
                        value: "ProductName"
                    },
                    {
                        text: "", // remove button
                        sortable: false
                    }
                ]
            }
        };
    },
    computed: {
        loading() {
            return this.$store.state.supplierStore.products.loading || this.$store.state.categoryStore.loading || this.$store.state.productStore.loading;
        },
        loadError() {
            return this.$store.state.supplierStore.products.loadError || this.$store.state.categoryStore.loadError || this.$store.state.productStore.loadError;
        },
        saving() {
            return this.$store.state.supplierStore.products.saving;
        },
        saveError() {
            return this.$store.state.supplierStore.products.saveError;
        },
        supplierProductsByCategory() {
            return this.$store.getters["supplierStore/supplierProductsByCategory"];
        },
        supplierProductsByIndividual() {
            let supplierProducts = this.$store.getters["supplierStore/supplierProductsByIndividual"];

            // load in the category data for each of these items so that we can display and sort on the CategoryName
            supplierProducts = supplierProducts.map(function(supplierProduct, index, arr) {
                let category = this.findCategoryForProduct(supplierProduct.ProductID);
                supplierProduct.CategoryName = category.Name;

                return supplierProduct;
            }, this);

            return supplierProducts;
        },
        supplierProductsByCategoryCategoryIDs() {
            return this.supplierProductsByCategory.map(function(supplierProduct, index, arr) {
                return supplierProduct.CategoryID;
            });
        },
        supplierProductsByIndividualProductIDs() {
            return this.supplierProductsByIndividual.map(function(supplierProduct, index, arr) {
                return supplierProduct.ProductID;
            });
        },
        unselectedCategories() {
            return this.$store.state.categoryStore.categories.filter(function(category, index, arr) {
                return category.Active && !this.supplierProductsByCategoryCategoryIDs.includes(category.ID);
            }, this);
        },
        unselectedProducts() {
            return this.$store.state.productStore.products.filter(function(product, index, arr) {
                if (!product.Active) {
                    return false;
                }

                // was the product selected individually?
                let productSelected = this.supplierProductsByIndividualProductIDs.includes(product.ID);
                let categorySelected = false;

                // don't need to evaluate this if we already know that the product was selected individually
                if (!productSelected) {
                    // was the product's category selected?
                    categorySelected = this.supplierProductsByCategoryCategoryIDs.includes(product.CategoryID);
                }

                return !(productSelected || categorySelected);
            }, this);
        }
    },
    methods: {
        cancelEditSupplierProducts() {
            this.$store.dispatch("supplierStore/showProductsEditDialog", {show: false});
            this.newCategory = null;
            this.newProduct = null;
        },
        saveEditedSupplierProducts() {
            var that = this;

            this.$store.dispatch("supplierStore/saveSupplierProducts", {
                supplierID: this.editedSupplier.ID,
                supplierProducts: this.$store.getters["supplierStore/supplierProducts"],
                finally: function() {
                    if (that.$store.state.supplierStore.products.saveError) {
                        // do not close the dialog
                        // display the error
                    }
                    else {
                        // close the dialog
                        that.cancelEditSupplierProducts();
                    }
                }
            });
        },
        loadSupplierProducts() {
            this.$store.dispatch("supplierStore/loadSupplierProducts");
        },
        addCategory() {
            this.$store.dispatch("supplierStore/addSupplierProduct", {
                SupplierID: this.editedSupplier.ID,
                CategoryID: this.newCategory.ID,
                CategoryName: this.newCategory.Name
            });

            this.newCategory = {};
        },
        removeCategory(category) {
            this.$store.dispatch("supplierStore/removeSupplierProduct", {
                CategoryID: category.CategoryID
            });
        },
        addProduct() {
            this.$store.dispatch("supplierStore/addSupplierProduct", {
                SupplierID: this.editedSupplier.ID,
                ProductID: this.newProduct.ID,
                ProductName: this.newProduct.Name
            });

            this.newProduct = {};
        },
        removeProduct(product) {
            this.$store.dispatch("supplierStore/removeSupplierProduct", {
                ProductID: product.ProductID
            });
        },
        findCategoryForProduct(productID) {
            let product = this.$store.state.productStore.products.find(product => product.ID === productID);
            return this.$store.state.categoryStore.categories.find(category => category.ID === product.CategoryID);
        }
    }
};
</script>

<style>

</style>
